import { useState, useEffect, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";

const id = uuidv4();

export function useDebounce(value, delay = 3000) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

export function formatString(str) {
  const noUnderscores = str.replace(/_/g, " ");
  return noUnderscores.charAt(0).toUpperCase() + noUnderscores.slice(1);
}

export function toSnakeCase(obj) {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`),
      value,
    ]),
  );
}

export function generateTimesAMPM() {
  const times = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let min = 0; min < 60; min += 15) {
      const suffix = hour >= 12 ? "PM" : "AM";
      const h12 = hour % 12 === 0 ? 12 : hour % 12;
      const mStr = String(min).padStart(2, "0");
      times.push(`${h12}:${mStr} ${suffix}`);
    }
  }
  return times;
}

export function parseLocalDateTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;

  const [year, month, day] = dateStr.split("-").map(Number);

  const normalized = timeStr.trim().toLowerCase();

  const isPM = normalized.endsWith("pm");
  const isAM = normalized.endsWith("am");

  const timePart = normalized.replace("am", "").replace("pm", "");
  let [hours, minutes] = timePart.split(":").map(Number);

  if (isPM && hours !== 12) hours += 12;
  if (isAM && hours === 12) hours = 0;

  return new Date(year, month - 1, day, hours, minutes);
}

export function parseLocalDate(dateStr) {
  if (!dateStr) return null;

  const [year, month, day] = dateStr.split("-").map(Number);

  return new Date(year, month - 1, day);
}

export function getEarliestAllowedDateTime(
  locations,
  currentIndex,
  bufferMinutes = 10,
) {
  const currentLocation = locations[currentIndex];
  if (!currentLocation) return null;

  for (let i = currentIndex - 1; i >= 0; i--) {
    const prevLoc = locations[i];

    const isValid =
      prevLoc &&
      typeof prevLoc.lat === "number" &&
      typeof prevLoc.lng === "number" &&
      prevLoc.date &&
      prevLoc.time;

    if (!isValid) continue;

    const prevDateTime = parseLocalDateTime(prevLoc.date, prevLoc.time);
    if (!prevDateTime) continue;

    const travelSeconds = currentLocation.travelDurationFromPrevious || 0;

    return new Date(
      prevDateTime.getTime() + travelSeconds * 1000 + bufferMinutes * 60 * 1000,
    );
  }

  return null;
}

export function formatDateToYMD(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(date.getDate()).padStart(2, "0")}`;
}

export function formatDateToMDY(date) {
  const [y, m, d] = date.split("-").map(Number);
  const localDate = new Date(y, m - 1, d);

  return new Intl.DateTimeFormat("en-US").format(localDate);
}

export function validateItineraryChronology(locations) {
  let isValid = true;
  let prevDateTime = null;

  locations.forEach((loc, idx) => {
    if (!loc.date || !loc.time) return;

    const currentDateTime = parseLocalDateTime(loc.date, loc.time);
    if (!currentDateTime) return;

    if (prevDateTime && currentDateTime < prevDateTime) {
      isValid = false;
    } else {
      prevDateTime = currentDateTime;
    }
  });

  return { isValid };
}

const normalizeTime = (timeStr) => {
  const [time, modifier] = timeStr.split(" ");

  let [hours, minutes] = time.split(":");

  if (modifier === "PM" && hours !== "12") {
    hours = String(Number(hours) + 12);
  }

  if (modifier === "AM" && hours === "12") {
    hours = "00";
  }

  hours = hours.padStart(2, "0");

  return `${hours}:${minutes}`;
};

export function getItineraryEndDate(locations) {
  if (locations.length < 2) return "";

  const last = locations[locations.length - 1];
  const prev = locations[locations.length - 2];

  if (!last.travelDurationFromPrevious || !prev.date || !prev.time) return "";

  const time24 = normalizeTime(prev.time);

  const rawDate = `${prev.date}T${time24}:00`;
  const prevDateTime = new Date(rawDate);

  const baseTime = prevDateTime?.getTime();
  const duration = Number(last?.travelDurationFromPrevious);

  if (!isFinite(baseTime) || !isFinite(duration)) {
    console.warn("Invalid inputs:", {
      prevDateTime,
      travelDurationFromPrevious: last?.travelDurationFromPrevious,
    });
  }

  const endTime =
    isFinite(baseTime) && isFinite(duration)
      ? new Date(baseTime + duration * 1000)
      : null;

  if (endTime) {
    return new Intl.DateTimeFormat("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
    }).format(endTime);
  }
}

const formatDate = (date) =>
  new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  }).format(date);

const formatTime = (date) =>
  new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
    .format(date)
    .replace(/\s+/g, "");

export function buildItineraryTimes(locations) {
  const results = [];

  let previousDepart = null;

  locations.forEach((loc, index) => {
    const isFirst = index === 0;
    const isLast = index === locations.length - 1;

    let arriveDate = "";
    let arriveTime = "";
    let departDate = "";
    let departTime = "";

    if (!isFirst && previousDepart && loc.travelDurationFromPrevious) {
      const arrival = new Date(
        previousDepart.getTime() + loc.travelDurationFromPrevious * 1000,
      );

      arriveDate = formatDate(arrival);
      arriveTime = formatTime(arrival);
    } else if (!isFirst && previousDepart && !loc.travelDurationFromPrevious) {
      arriveDate = formatDate(previousDepart.getTime());
      arriveTime = formatTime(previousDepart.getTime());
    }

    if (!isLast && loc.date && loc.time) {
      const depart = parseLocalDateTime(loc.date, loc.time);

      departDate = formatDate(depart);
      departTime = formatTime(depart);
      previousDepart = depart;
    }

    results.push({
      arrive_date: arriveDate,
      arrive_time: arriveTime,
      depart_date: departDate,
      depart_time: departTime,
    });
  });

  return results;
}

export function formatUtcDate(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "UTC",
  }).formatToParts(date);

  const get = (type) => parts.find((p) => p.type === type)?.value;

  return `${get("weekday")}, ${get("day")} ${get("month")} ${get("year")} ${get("hour")}:${get("minute")}:${get("second")} GMT`;
}
export function useSelectScroll(targetValue) {
  const handleDropdownOpen = useCallback(() => {
    let checkCount = 0;

    const checkDOM = () => {
      const items = document.querySelectorAll(
        '.mantine-Select-option, [role="option"], .mantine-Combobox-option',
      );

      const target = Array.from(items).find(
        (el) => el.textContent?.trim() === targetValue.trim(),
      );

      if (target) {
        const scrollableParent =
          target.closest(".mantine-ScrollArea-viewport") ||
          target.closest('[role="listbox"]') ||
          target.closest(".mantine-Select-dropdown");

        if (scrollableParent && scrollableParent.clientHeight > 0) {
          const targetOffset = target.offsetTop;
          const parentHeight = scrollableParent.clientHeight;
          const targetHeight = target.clientHeight;

          const scrollToValue =
            targetOffset - parentHeight / 2 + targetHeight / 2;

          scrollableParent.scrollTop = scrollToValue;

          return;
        }
      }

      if (checkCount < 50) {
        checkCount++;
        requestAnimationFrame(checkDOM);
      }
    };

    checkDOM();
  }, [targetValue]);

  return handleDropdownOpen;
}

function addHours(dateStr, timeStr, hoursToAdd) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d);

  const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!match) return { error: "Invalid Time Format" };

  let [, hours, minutes, modifier] = match;
  hours = parseInt(hours, 10);
  minutes = parseInt(minutes, 10);
  modifier = modifier.toUpperCase();

  let hours24 = hours;
  if (modifier === "PM" && hours !== 12) {
    hours24 = hours + 12;
  } else if (modifier === "AM" && hours === 12) {
    hours24 = 0;
  }

  date.setHours(hours24, minutes, 0, 0);

  date.setHours(date.getHours() + parseInt(hoursToAdd, 10));

  return {
    date: formatDate(date),
    time: formatTime(date),
  };
}

export function normalizeLocations(locations, date, duration) {
  if (!locations?.length) return [];

  const newDateAndTime = addHours(date, "8:00 AM", duration);
  const formattedDate = formatDate(new Date(date.replace(/-/g, "\/")));
  const base = locations[0];
  const { id, ...rest } = base;

  return [
    {
      location: {
        ...rest,
        id: uuidv4(),
        type: "departure",
        locationType: "departure",
      },

      arrive_date: "",
      arrive_time: "",
      depart_date: formattedDate,
      depart_time: "8:00AM",
    },
    {
      location: {
        ...rest,
        id: uuidv4(),
        type: "stop",
        locationType: "stop",
      },
      arrive_date: formattedDate,
      arrive_time: "8:00AM",
      depart_date: formattedDate,
      depart_time: newDateAndTime.time,
    },
    {
      location: {
        ...rest,
        id: uuidv4(),
        type: "destination",
        locationType: "destination",
      },
      arrive_date: formattedDate,
      arrive_time: newDateAndTime.time,
      depart_date: "",
      depart_time: "",
    },
  ];
}

export const groupTypes = [
  {
    label: "Academic - K-12, Charter Schools, College/Uni",
    value: "Academic - K-12, Charter Schools, College/Uni",
  },
  { label: "Athletics & Team Travel", value: "Athletics & Team Travel" },
  {
    label: "Camps & After School Programs",
    value: "Camps & After School Programs",
  },
  {
    label: "Conventions, Large Events & Festivals",
    value: "Conventions, Large Events & Festivals",
  },
  {
    label: "Corporate Travel & Meeting Management",
    value: "Corporate Travel & Meeting Management",
  },
  {
    label: "Emergency / Disaster Relief (Priority Access)",
    value: "Emergency / Disaster Relief (Priority Access)",
  },
  { label: "Faith Based Travel", value: "Faith Based Travel" },
  {
    label: "Government, Political Campaigns & Military",
    value: "Government, Political Campaigns & Military",
  },
  {
    label: "Non-Profit and Associations",
    value: "Non-Profit and Associations",
  },
  {
    label: "Recurring Shuttles - Employee, Labor, etc",
    value: "Recurring Shuttles - Employee, Labor, etc",
  },
  {
    label: "Social, Family, Fraternity/Sorority",
    value: "Social, Family, Fraternity/Sorority",
  },
  {
    label: "Tour Operators & Travel Industry",
    value: "Tour Operators & Travel Industry",
  },
  { label: "Wedding", value: "Wedding" },
  { label: "Other", value: "Other" },
];

export const busTypes = [
  {
    label: "Motorcoach: up to 56 passengers",
    value: "Deluxe Motorcoach",
  },
  { label: "Mini Bus: 20 to 40 passengers", value: "Mini Bus" },
  { label: "Sprinter: up to 14 passengers", value: "Sprinter" },
  { label: "School Bus: up to 44 passengers", value: "School Bus" },
  { label: "Executive Coach: up to 40", value: "Executive Coach" },
];

export const majorCities = [
  { city: "New York", region: "NY", country: "USA" },
  { city: "Los Angeles", region: "CA", country: "USA" },
  { city: "Chicago", region: "IL", country: "USA" },
  { city: "Houston", region: "TX", country: "USA" },
  { city: "Phoenix", region: "AZ", country: "USA" },
  { city: "Philadelphia", region: "PA", country: "USA" },
  { city: "San Antonio", region: "TX", country: "USA" },
  { city: "San Diego", region: "CA", country: "USA" },
  { city: "Dallas", region: "TX", country: "USA" },
  { city: "San Jose", region: "CA", country: "USA" },
  { city: "Austin", region: "TX", country: "USA" },
  { city: "Jacksonville", region: "FL", country: "USA" },
  { city: "Fort Worth", region: "TX", country: "USA" },
  { city: "Columbus", region: "OH", country: "USA" },
  { city: "Charlotte", region: "NC", country: "USA" },
  { city: "San Francisco", region: "CA", country: "USA" },
  { city: "Indianapolis", region: "IN", country: "USA" },
  { city: "Seattle", region: "WA", country: "USA" },
  { city: "Denver", region: "CO", country: "USA" },
  { city: "Washington", region: "DC", country: "USA" },
  { city: "Boston", region: "MA", country: "USA" },
  { city: "Nashville", region: "TN", country: "USA" },
  { city: "Detroit", region: "MI", country: "USA" },
  { city: "Oklahoma City", region: "OK", country: "USA" },
  { city: "Portland", region: "OR", country: "USA" },
  { city: "Las Vegas", region: "NV", country: "USA" },
  { city: "Memphis", region: "TN", country: "USA" },
  { city: "Louisville", region: "KY", country: "USA" },
  { city: "Baltimore", region: "MD", country: "USA" },
  { city: "Milwaukee", region: "WI", country: "USA" },
  { city: "Albuquerque", region: "NM", country: "USA" },
  { city: "Tucson", region: "AZ", country: "USA" },
  { city: "Fresno", region: "CA", country: "USA" },
  { city: "Sacramento", region: "CA", country: "USA" },
  { city: "Mesa", region: "AZ", country: "USA" },
  { city: "Kansas City", region: "MO", country: "USA" },
  { city: "Atlanta", region: "GA", country: "USA" },
  { city: "Omaha", region: "NE", country: "USA" },
  { city: "Colorado Springs", region: "CO", country: "USA" },
  { city: "Raleigh", region: "NC", country: "USA" },
  { city: "Miami", region: "FL", country: "USA" },
  { city: "Long Beach", region: "CA", country: "USA" },
  { city: "Virginia Beach", region: "VA", country: "USA" },
  { city: "Oakland", region: "CA", country: "USA" },
  { city: "Minneapolis", region: "MN", country: "USA" },
  { city: "Tulsa", region: "OK", country: "USA" },
  { city: "Arlington", region: "TX", country: "USA" },
  { city: "Tampa", region: "FL", country: "USA" },
  { city: "New Orleans", region: "LA", country: "USA" },
  { city: "Wichita", region: "KS", country: "USA" },
  { city: "Cleveland", region: "OH", country: "USA" },
  { city: "Bakersfield", region: "CA", country: "USA" },
  { city: "Aurora", region: "CO", country: "USA" },
  { city: "Anaheim", region: "CA", country: "USA" },
  { city: "Honolulu", region: "HI", country: "USA" },
  { city: "Santa Ana", region: "CA", country: "USA" },
  { city: "Riverside", region: "CA", country: "USA" },
  { city: "Corpus Christi", region: "TX", country: "USA" },
  { city: "Lexington", region: "KY", country: "USA" },
  { city: "Stockton", region: "CA", country: "USA" },
  { city: "Saint Paul", region: "MN", country: "USA" },
  { city: "Cincinnati", region: "OH", country: "USA" },
  { city: "Saint Louis", region: "MO", country: "USA" },
  { city: "Pittsburgh", region: "PA", country: "USA" },
  { city: "Greensboro", region: "NC", country: "USA" },
  { city: "Anchorage", region: "AK", country: "USA" },
  { city: "Plano", region: "TX", country: "USA" },
  { city: "Lincoln", region: "NE", country: "USA" },
  { city: "Orlando", region: "FL", country: "USA" },
  { city: "Irvine", region: "CA", country: "USA" },
  { city: "Newark", region: "NJ", country: "USA" },
  { city: "Durham", region: "NC", country: "USA" },
  { city: "Chula Vista", region: "CA", country: "USA" },
  { city: "Toledo", region: "OH", country: "USA" },
  { city: "Fort Wayne", region: "IN", country: "USA" },
  { city: "St. Petersburg", region: "FL", country: "USA" },
  { city: "Laredo", region: "TX", country: "USA" },
  { city: "Jersey City", region: "NJ", country: "USA" },
  { city: "Chandler", region: "AZ", country: "USA" },
  { city: "Madison", region: "WI", country: "USA" },
  { city: "Lubbock", region: "TX", country: "USA" },
  { city: "Scottsdale", region: "AZ", country: "USA" },
  { city: "Reno", region: "NV", country: "USA" },
  { city: "Buffalo", region: "NY", country: "USA" },
  { city: "Gilbert", region: "AZ", country: "USA" },
  { city: "Glendale", region: "AZ", country: "USA" },
  { city: "North Las Vegas", region: "NV", country: "USA" },
  { city: "Winston-Salem", region: "NC", country: "USA" },
  { city: "Chesapeake", region: "VA", country: "USA" },
  { city: "Norfolk", region: "VA", country: "USA" },
  { city: "Fremont", region: "CA", country: "USA" },
  { city: "Garland", region: "TX", country: "USA" },
  { city: "Irving", region: "TX", country: "USA" },
  { city: "Hialeah", region: "FL", country: "USA" },
  { city: "Richmond", region: "VA", country: "USA" },
  { city: "Boise", region: "ID", country: "USA" },
  { city: "Spokane", region: "WA", country: "USA" },
  { city: "Baton Rouge", region: "LA", country: "USA" },
  { city: "Des Moines", region: "IA", country: "USA" },
  { city: "Tacoma", region: "WA", country: "USA" },
  { city: "San Bernardino", region: "CA", country: "USA" },
  { city: "Modesto", region: "CA", country: "USA" },
  { city: "Fontana", region: "CA", country: "USA" },
  { city: "Santa Clarita", region: "CA", country: "USA" },
  { city: "Oxnard", region: "CA", country: "USA" },
  { city: "Fayetteville", region: "NC", country: "USA" },
  { city: "Moreno Valley", region: "CA", country: "USA" },
  { city: "Huntington Beach", region: "CA", country: "USA" },
  { city: "Yonkers", region: "NY", country: "USA" },
  { city: "Glendale", region: "CA", country: "USA" },
  { city: "Aurora", region: "IL", country: "USA" },
  { city: "Montgomery", region: "AL", country: "USA" },
  { city: "Akron", region: "OH", country: "USA" },
  { city: "Augusta", region: "GA", country: "USA" },
  { city: "Amarillo", region: "TX", country: "USA" },
  { city: "Mobile", region: "AL", country: "USA" },
  { city: "Grand Rapids", region: "MI", country: "USA" },
  { city: "Salt Lake City", region: "UT", country: "USA" },
  { city: "Huntsville", region: "AL", country: "USA" },
  { city: "Tallahassee", region: "FL", country: "USA" },
  { city: "Grand Prairie", region: "TX", country: "USA" },
  { city: "Overland Park", region: "KS", country: "USA" },
  { city: "Knoxville", region: "TN", country: "USA" },
  { city: "Worcester", region: "MA", country: "USA" },
  { city: "Birmingham", region: "AL", country: "USA" },
  { city: "Juneau", region: "AK", country: "USA" },
  { city: "Tempe", region: "AZ", country: "USA" },
  { city: "Peoria", region: "AZ", country: "USA" },
  { city: "Surprise", region: "AZ", country: "USA" },
  { city: "Little Rock", region: "AR", country: "USA" },
  { city: "Fayetteville", region: "AR", country: "USA" },
  { city: "Santa Rosa", region: "CA", country: "USA" },
  { city: "Salinas", region: "CA", country: "USA" },
  { city: "Pomona", region: "CA", country: "USA" },
  { city: "Pasadena", region: "CA", country: "USA" },
  { city: "Escondido", region: "CA", country: "USA" },
  { city: "Sunnyvale", region: "CA", country: "USA" },
  { city: "Hayward", region: "CA", country: "USA" },
  { city: "Torrance", region: "CA", country: "USA" },
  { city: "Fullerton", region: "CA", country: "USA" },
  { city: "Visalia", region: "CA", country: "USA" },
  { city: "Boulder", region: "CO", country: "USA" },
  { city: "Fort Collins", region: "CO", country: "USA" },
  { city: "Bridgeport", region: "CT", country: "USA" },
  { city: "New Haven", region: "CT", country: "USA" },
  { city: "Stamford", region: "CT", country: "USA" },
  { city: "Dover", region: "DE", country: "USA" },
  { city: "Wilmington", region: "DE", country: "USA" },
  { city: "Fort Lauderdale", region: "FL", country: "USA" },
  { city: "Cape Coral", region: "FL", country: "USA" },
  { city: "Pembroke Pines", region: "FL", country: "USA" },
  { city: "Hollywood", region: "FL", country: "USA" },
  { city: "Gainesville", region: "FL", country: "USA" },
  { city: "Savannah", region: "GA", country: "USA" },
  { city: "Athens", region: "GA", country: "USA" },
  { city: "Columbus", region: "GA", country: "USA" },
  { city: "Macon", region: "GA", country: "USA" },
  { city: "Idaho Falls", region: "ID", country: "USA" },
  { city: "Naperville", region: "IL", country: "USA" },
  { city: "Rockford", region: "IL", country: "USA" },
  { city: "Springfield", region: "IL", country: "USA" },
  { city: "Champaign", region: "IL", country: "USA" },
  { city: "Evansville", region: "IN", country: "USA" },
  { city: "South Bend", region: "IN", country: "USA" },
  { city: "Cedar Rapids", region: "IA", country: "USA" },
  { city: "Davenport", region: "IA", country: "USA" },
  { city: "Sioux City", region: "IA", country: "USA" },
  { city: "Topeka", region: "KS", country: "USA" },
  { city: "Olathe", region: "KS", country: "USA" },
  { city: "Lawrence", region: "KS", country: "USA" },
  { city: "Bowling Green", region: "KY", country: "USA" },
  { city: "Shreveport", region: "LA", country: "USA" },
  { city: "Lafayette", region: "LA", country: "USA" },
  { city: "Portland", region: "ME", country: "USA" },
  { city: "Annapolis", region: "MD", country: "USA" },
  { city: "Cambridge", region: "MA", country: "USA" },
  { city: "Lowell", region: "MA", country: "USA" },
  { city: "Springfield", region: "MA", country: "USA" },
  { city: "Flint", region: "MI", country: "USA" },
  { city: "Lansing", region: "MI", country: "USA" },
  { city: "Ann Arbor", region: "MI", country: "USA" },
  { city: "Rochester", region: "MN", country: "USA" },
  { city: "Duluth", region: "MN", country: "USA" },
  { city: "Jackson", region: "MS", country: "USA" },
  { city: "Columbia", region: "MO", country: "USA" },
  { city: "Springfield", region: "MO", country: "USA" },
  { city: "Billings", region: "MT", country: "USA" },
  { city: "Missoula", region: "MT", country: "USA" },
  { city: "Helena", region: "MT", country: "USA" },
  { city: "Sioux Falls", region: "SD", country: "USA" },
  { city: "Rapid City", region: "SD", country: "USA" },
  { city: "Fargo", region: "ND", country: "USA" },
  { city: "Bismarck", region: "ND", country: "USA" },
  { city: "Charleston", region: "SC", country: "USA" },
  { city: "Columbia", region: "SC", country: "USA" },
  { city: "Greenville", region: "SC", country: "USA" },
  { city: "Chattanooga", region: "TN", country: "USA" },
  { city: "Clarksville", region: "TN", country: "USA" },
  { city: "Murfreesboro", region: "TN", country: "USA" },
  { city: "El Paso", region: "TX", country: "USA" },
  { city: "McAllen", region: "TX", country: "USA" },
  { city: "Brownsville", region: "TX", country: "USA" },
  { city: "Frisco", region: "TX", country: "USA" },
  { city: "Denton", region: "TX", country: "USA" },
  { city: "Waco", region: "TX", country: "USA" },
  { city: "Provo", region: "UT", country: "USA" },
  { city: "Ogden", region: "UT", country: "USA" },
  { city: "St. George", region: "UT", country: "USA" },
  { city: "Burlington", region: "VT", country: "USA" },
  { city: "Roanoke", region: "VA", country: "USA" },
  { city: "Alexandria", region: "VA", country: "USA" },
  { city: "Bellevue", region: "WA", country: "USA" },
  { city: "Everett", region: "WA", country: "USA" },
  { city: "Yakima", region: "WA", country: "USA" },
  { city: "Charleston", region: "WV", country: "USA" },
  { city: "Huntington", region: "WV", country: "USA" },
  { city: "Green Bay", region: "WI", country: "USA" },
  { city: "Kenosha", region: "WI", country: "USA" },
  { city: "Cheyenne", region: "WY", country: "USA" },
  { city: "Casper", region: "WY", country: "USA" },

  { city: "Toronto", region: "ON", country: "Canada" },
  { city: "Mississauga", region: "ON", country: "Canada" },
  { city: "Brampton", region: "ON", country: "Canada" },
  { city: "Markham", region: "ON", country: "Canada" },
  { city: "Vaughan", region: "ON", country: "Canada" },
  { city: "Richmond Hill", region: "ON", country: "Canada" },
  { city: "Oakville", region: "ON", country: "Canada" },
  { city: "Burlington", region: "ON", country: "Canada" },
  { city: "Milton", region: "ON", country: "Canada" },
  { city: "Pickering", region: "ON", country: "Canada" },
  { city: "Ajax", region: "ON", country: "Canada" },
  { city: "Whitby", region: "ON", country: "Canada" },
  { city: "Oshawa", region: "ON", country: "Canada" },
  { city: "Hamilton", region: "ON", country: "Canada" },
  { city: "Kingston", region: "ON", country: "Canada" },
  { city: "Ottawa", region: "ON", country: "Canada" },
  { city: "Montreal", region: "QC", country: "Canada" },
  { city: "Quebec City", region: "QC", country: "Canada" },
  { city: "Gatineau", region: "QC", country: "Canada" },
  { city: "Vancouver", region: "BC", country: "Canada" },
  { city: "Surrey", region: "BC", country: "Canada" },
  { city: "Burnaby", region: "BC", country: "Canada" },
  { city: "Richmond", region: "BC", country: "Canada" },
  { city: "Calgary", region: "AB", country: "Canada" },
  { city: "Edmonton", region: "AB", country: "Canada" },
  { city: "Winnipeg", region: "MB", country: "Canada" },
  { city: "Saskatoon", region: "SK", country: "Canada" },
  { city: "Regina", region: "SK", country: "Canada" },
  { city: "Halifax", region: "NS", country: "Canada" },
  { city: "St. John's", region: "NL", country: "Canada" },
  { city: "Victoria", region: "BC", country: "Canada" },
  { city: "Kelowna", region: "BC", country: "Canada" },
  { city: "Lethbridge", region: "AB", country: "Canada" },
  { city: "Waterloo", region: "ON", country: "Canada" },
  { city: "Kitchener", region: "ON", country: "Canada" },
  { city: "Guelph", region: "ON", country: "Canada" },
  { city: "Cambridge", region: "ON", country: "Canada" },
  { city: "London", region: "ON", country: "Canada" },
  { city: "Sudbury", region: "ON", country: "Canada" },
  { city: "Thunder Bay", region: "ON", country: "Canada" },
  { city: "Saint John", region: "NB", country: "Canada" },
  { city: "Moncton", region: "NB", country: "Canada" },
  { city: "Fredericton", region: "NB", country: "Canada" },
];

export const globalTableReset = `
  #get-a-quote td, 
  #get-a-quote th, 
  #get-a-quote tr, 
  #get-a-quote table {
    border: 0 none transparent !important;
    padding: 0 !important;
    outline: none !important;
    box-shadow: none !important;
    background-image: none !important;
    border-collapse: collapse !important;
    border-spacing: 0 !important;
  }
  
  /* This targets the specific Mantine classes just in case */
  #get-a-quote .mantine-DatePicker-day,
  #get-a-quote .mantine-DatePicker-weekday,
  #get-a-quote .mantine-DatePicker-cell {
    border: 0 none transparent !important;
    background: transparent !important;
  }
`;

export const svgIconReset = `
  #get-a-quote svg {
    display: inline !important;
  }
`;
